
  import { defineComponent, ref, computed } from 'vue';
  import { useStore } from 'vuex';
  import { Actions } from '@/store/enums/StoreEnums';
  import { useI18n } from 'vue-i18n/index';
  import CustomDatePicker from '@/components/search/CustomDatePicker.vue';

  type Change = 'increase' | 'decrease' | 'noChange';

  interface StatsData {
    type: 'land' | 'building';
    source: 'map' | 'manual';
    count: number;
    countChange: Change;
    countPercentage: number;
  }

  export default defineComponent({
    name: 'hisba-realty-stats-widget',
    props: {
      widgetClasses: { type: String, default: 'card-xxl-stretch mb-xl-8' },
    },
    components: {
      CustomDatePicker,
    },
    async setup() {
      const { t, te } = useI18n();
      const store = useStore();
      const loading = ref(true);
      const translate = (text) => {
        if (te(text)) {
          return t(text);
        } else {
          return text;
        }
      };
      const selectedPeriod = ref('last-7-days');
      const customDateFrom = ref('');
      const customDateTo = ref('');
      const showDatePicker = ref(false);

      await store.dispatch(Actions.GET_IVE_SUMMARY_DASHBOARD, {
        dateRange: selectedPeriod.value,
        showTrend: false,
      });
      loading.value = false;

      const data = computed(() => store.getters.iveSummaryDashboard);

      const pick = (t: StatsData['type'], s: StatsData['source']) =>
        data.value?.data?.find(
          (x: StatsData) => x.type === t && x.source === s
        ) || null;

      const mapLandData = computed<StatsData | null>(() => pick('land', 'map'));
      const mapBuildingData = computed<StatsData | null>(() =>
        pick('building', 'map')
      );
      const manualLandData = computed<StatsData | null>(() =>
        pick('land', 'manual')
      );
      const manualBuildingData = computed<StatsData | null>(() =>
        pick('building', 'manual')
      );

      const showCustomDatePicker = () => {
        showDatePicker.value = true;
        selectedPeriod.value = 'custom';
      };

      const applyCustomDates = async () => {
        if (!customDateFrom.value || !customDateTo.value) {
          alert('Please select both dates');
          return;
        }
        showDatePicker.value = false;
        await selectPeriod('custom');
      };

      const selectPeriod = async (period: string) => {
        selectedPeriod.value = period;
        loading.value = true;
        const obj = {
          dateRange: period,
          showTrend: false,
          dateFrom: '',
          dateTo: '',
        };
        if (period === 'custom' && customDateFrom.value && customDateTo.value) {
          obj.dateFrom = customDateFrom.value;
          obj.dateTo = customDateTo.value;
        }

        await store.dispatch(Actions.GET_IVE_SUMMARY_DASHBOARD, obj);
        loading.value = false;
      };

      const periodLabel = computed(() =>
        selectedPeriod.value === 'last-7-days'
          ? 'Last 7 Days'
          : selectedPeriod.value === 'last-30-days'
          ? 'Last 30 Days'
          : 'Yesterday'
      );

      const formatCount = (n: number) => n.toLocaleString();

      const trendSymbol = (c: Change) =>
        c === 'increase' ? '↑' : c === 'decrease' ? '↓' : '↔';
      const trendClass = (c: Change) =>
        c === 'increase' ? 'up' : c === 'decrease' ? 'down' : 'flat';

      return {
        loading,
        periodLabel,
        selectPeriod,
        mapLandData,
        mapBuildingData,
        manualLandData,
        manualBuildingData,
        formatCount,
        trendSymbol,
        trendClass,
        selectedPeriod,
        translate,
        showDatePicker,
        customDateFrom,
        customDateTo,
        showCustomDatePicker,
        applyCustomDates,
      };
    },
  });
